import { brandOf, domainOf, compact, matchDomainByName, businessName } from "./domains";
import { classifyBusiness, BUILT_IN_LISTS } from "./businessTypes/classifyBusiness";

// The counting shared by marketOverview() and advertiserOverview(): which
// searches there were, who each result row belongs to, what each advertiser
// was seen in, and the ranking. Both pages call these, so an advertiser's
// rank and visibility can never differ between them.
// Pure: no React, no database, no browser, no clock.

// Channels that count towards visibility, ranking and multi-channel.
// Shopping and Local Services Ads are shown, but never move an advertiser up
// the table.
export const CORE_CHANNELS = ["ppc", "local", "seo"];
export const CHANNELS = ["ppc", "local", "seo", "shopping", "lsa"];
const CHANNEL_OF_ROW = { ppc: "ppc", gbp: "local", seo: "seo", shopping: "shopping", lsa: "lsa" };

// "Highest average position" (and "average position") only count advertisers
// seen at least this often.
export const MIN_SEARCHES_FOR_POSITION = 2;

const EMPTY_FILTERS = { device: "all", keywords: [], postcodes: [], runIds: [], businessTypes: [], advertisers: [] };

// Everything both pages count from, for one set of filters:
// { job, f, towns, searches, scoped, completed, of, ofByDevice, searchByKey,
//   identities, advertisers, stats, serp, shoppingUnidentified }
// Each completed search also carries seenChannels: the channels anyone
// (named or not) appeared in.
export function prepareReport(data, filters = {}) {
  const job = data.job || {};
  const f = normaliseFilters(filters);

  // ---- Searches: every planned search, then which were done or failed ----
  const searches = searchOutcomes(job, data.samples, data.appearances, data.searches);

  const scoped = searches.filter(s => inScope(s, f));
  const completed = scoped.filter(s => s.status === "completed");
  const of = completed.length;
  const searchByKey = new Map(completed.map(s => [s.key, s]));

  // ---- Advertisers: who each row belongs to (whole job, before filters) ----
  const identities = identifyAll(data);
  const advertisers = buildAdvertisers(identities, overridesByKey(data.typeOverrides));

  // ---- Counts over the completed searches in the filters ----
  const stats = {};
  const serp = {};
  let shoppingUnidentified = 0;
  for (const search of completed) {
    const firstOverall = {};
    for (const row of search.rows) {
      const id = identities.get(row);
      if (!id) continue;
      const channel = id.channel;
      firstOverall[channel] = minOf(firstOverall[channel], numberOrNull(row.raw_data?.pos_overall));

      if (id.unidentified) {
        if (channel === "shopping") shoppingUnidentified++;
        continue;
      }
      const s = stats[id.key] || (stats[id.key] = newStats());
      const pos = numberOrNull(row.raw_data?.pos);
      const map = s.channels[channel];
      map.set(search.key, map.has(search.key) ? minOf(map.get(search.key), pos) : pos);
      if (channel === "shopping" && hasPrice(row.raw_data)) s.withPrice.add(search.key);
      if (CORE_CHANNELS.includes(channel)) {
        s.coreSearches.add(search.key);
        if (search.device) s.deviceSearches[search.device]?.add(search.key);
      }
      s.keywords.add(search.keyword);
      s.postcodes.add(search.postcode);
      s.firstSeenAt = earliest(s.firstSeenAt, row.created_at);
      s.lastSeenAt = latest(s.lastSeenAt, row.created_at);
    }
    search.seenChannels = new Set(Object.keys(firstOverall));
    for (const [channel, first] of Object.entries(firstOverall)) {
      const block = serp[channel] || (serp[channel] = { shownIn: 0, starts: [] });
      block.shownIn++;
      if (first != null) block.starts.push(first);
    }
  }

  const ofByDevice = {
    mobile: completed.filter(s => s.device === "mobile").length,
    desktop: completed.filter(s => s.device === "desktop").length,
  };

  return {
    job, f, towns: data.towns || {}, searches, scoped, completed, of, ofByDevice, searchByKey,
    identities, advertisers, stats, serp, shoppingUnidentified,
  };
}

// The ranked advertiser rows: advertisers seen in these searches for whom
// keep(advertiser) is true, most visible first, numbered from 1.
// Each row also carries visibilityAppearances (removed before returning).
export function rankAdvertisers(report, keep = () => true) {
  const { advertisers, stats, of, ofByDevice } = report;
  const rows = Object.values(advertisers)
    .filter(a => stats[a.key])
    .filter(keep)
    .map(a => advertiserRow(a, stats[a.key], of, ofByDevice));
  rows.sort(compareAdvertisers);
  rows.forEach((row, i) => { row.rank = i + 1; });
  return rows;
}

// Does this advertiser pass the Business type filter?
export function passesTypeFilter(advertiser, f) {
  return f.businessTypes.length === 0 || f.businessTypes.includes(advertiser.businessType.type);
}

// Does this advertiser pass the Advertisers filter (keys; empty = everyone)?
export function passesAdvertiserFilter(advertiser, f) {
  return f.advertisers.length === 0 || f.advertisers.includes(advertiser.key);
}

// Both advertiser filters: Business type AND Advertisers.
export function passesFilters(advertiser, f) {
  return passesTypeFilter(advertiser, f) && passesAdvertiserFilter(advertiser, f);
}

// The Advertisers filter's list: everyone seen in the whole job (no filters),
// most visible first: [{ key, displayName }].
export function advertiserOptions(data) {
  const report = prepareReport(data);
  return rankAdvertisers(report).map(r => ({ key: r.key, displayName: r.displayName }));
}

// The job, filters, sampling, coverage and sources blocks, the same on every page.
export function reportHeader(report, samples) {
  const { job, f, scoped, of } = report;
  const scopedSamples = (samples || []).filter(s => f.runIds.length === 0 || f.runIds.includes(s.run_id));
  const planned = scopedSamples.map(s => s.planned_at).filter(Boolean).sort((a, b) => Date.parse(a) - Date.parse(b));
  const isOldJob = !job.samples_per_run;
  return {
    job: {
      id: job.id || null,
      name: job.name || null,
      niche: job.niches?.name || null,
      subGroup: job.sub_groups?.name || null,
      status: jobStatus(job.status, job.stop_reason),
      stopReason: job.stop_reason || null,
    },
    filters: f,
    sampling: {
      auditType: "one_off",
      samplesPerRun: job.samples_per_run || job.rounds || null,
      spacing: job.sample_spacing || job.frequency || null,
      devices: job.devices || [],
      firstSampleAt: isOldJob ? job.started_at || null : planned[0] || null,
      lastSampleAt: isOldJob ? null : planned[planned.length - 1] || null,
    },
    coverage: {
      searchesPlanned: scoped.length,
      searchesCompleted: of,
      searchesFailed: scoped.filter(s => s.status === "failed").length,
      basis: scoped.some(s => s.inferred) ? "inferred" : "recorded",
    },
    sources: {
      runningJobs: {
        used: true,
        runs: new Set(scoped.map(s => s.runId).filter(Boolean)).size,
        samples: new Set(scoped.map(s => s.group)).size,
        searches: of,
      },
      adAccount: { used: false, connected: false },
      inHouse: { used: false, connected: false },
    },
  };
}

// The job's keywords and postcodes left after the Keywords / Postcodes filters.
export function scopedKeywordsAndPostcodes(report) {
  const { job, f } = report;
  return {
    keywords: (job.keywords || []).filter(k => f.keywords.length === 0 || f.keywords.includes(k)),
    postcodes: (job.locations || []).filter(p => f.postcodes.length === 0 || f.postcodes.includes(p)),
  };
}

// Keywords, most completed searches first (ties keep the job's order).
export function keywordsBySearches(keywords, completed) {
  return keywords
    .map((keyword, order) => ({ keyword, searches: completed.filter(s => s.keyword === keyword).length, order }))
    .sort((a, b) => b.searches - a.searches || a.order - b.order)
    .map(({ keyword, searches }) => ({ keyword, searches }));
}

// The appearances left after the Business type and Advertisers filters, for
// the other report pages (PPC ...), so every page shows the same advertisers.
// Rows that can't be tied to an advertiser are left out when a filter is on.
export function filterAppearancesByType(data, businessTypes, advertiserKeys = []) {
  const appearances = data.appearances || [];
  const f = normaliseFilters({ businessTypes, advertisers: advertiserKeys });
  if (f.businessTypes.length === 0 && f.advertisers.length === 0) return appearances;
  const identities = identifyAll(data);
  const advertisers = buildAdvertisers(identities, overridesByKey(data.typeOverrides));
  return appearances.filter(row => {
    const id = identities.get(row);
    return Boolean(id && !id.unidentified && passesFilters(advertisers[id.key], f));
  });
}

// A stopped job is partial: it must never be described as finished.
// A job the Worker stopped because Oxylabs refused it (stop_reason set) is
// "error", so a broken login is never shown as an empty market.
export function jobStatus(status, stopReason) {
  if (status === "running") return "in_progress";
  if (status === "stopped") return stopReason ? "error" : "stopped";
  return "complete";
}

// ---------- Filters ----------

export function normaliseFilters(filters) {
  const list = value => (Array.isArray(value) ? value.filter(v => v != null && v !== "") : []);
  return {
    device: filters.device === "mobile" || filters.device === "desktop" ? filters.device : EMPTY_FILTERS.device,
    keywords: list(filters.keywords),
    postcodes: list(filters.postcodes),
    runIds: list(filters.runIds),
    businessTypes: list(filters.businessTypes),
    advertisers: list(filters.advertisers),
  };
}

function inScope(search, f) {
  if (f.device !== "all" && search.device !== f.device) return false;
  if (f.keywords.length > 0 && !f.keywords.includes(search.keyword)) return false;
  if (f.postcodes.length > 0 && !f.postcodes.includes(search.postcode)) return false;
  if (f.runIds.length > 0 && !f.runIds.includes(search.runId)) return false;
  return true;
}

export function overridesByKey(typeOverrides) {
  if (!typeOverrides) return {};
  if (!Array.isArray(typeOverrides)) return typeOverrides;
  const out = {};
  for (const o of typeOverrides) if (o && o.key && o.type) out[o.key] = o.type;
  return out;
}

// ---------- Searches ----------

// Every planned search of one job with its status (completed, failed, skipped,
// pending), and inferred: true where it was worked out from the data
// (coverage.basis). rows: the job's appearances; only their keyword,
// location, round, sample_id and device are needed to place them. records:
// the job's "searches" rows. A search with a record also carries finishedAt.
// Shared by the reports and the niche roll-up (industryRollup.js).
export function searchOutcomes(job, samples, rows, records) {
  const searches = planSearches(job || {}, samples || []);
  attachRows(searches, rows || []);
  markOutcomes(searches, records || [], (job || {}).status);
  return searches;
}

function searchKey(group, keyword, postcode, device) {
  return [group, keyword, postcode, device || ""].join("|");
}

// One search = keyword x postcode x device x sample, in the Worker's order
// (keyword, then postcode, then device). The first completed_units of each
// sample are done. Old jobs (no samples) use rounds and have no device.
function planSearches(job, samples) {
  const keywords = job.keywords || [];
  const postcodes = job.locations || [];
  const searches = [];

  if (job.samples_per_run) {
    const devices = job.devices && job.devices.length > 0 ? job.devices : [null];
    const perSample = keywords.length * postcodes.length * devices.length;
    const ordered = [...samples].sort((a, b) => (a.sample_number || 0) - (b.sample_number || 0));
    for (const sample of ordered) {
      for (let i = 0; i < perSample; i++) {
        const keyword = keywords[Math.floor(i / (postcodes.length * devices.length))];
        const postcode = postcodes[Math.floor(i / devices.length) % postcodes.length];
        const device = devices[i % devices.length];
        searches.push({
          key: searchKey(sample.id, keyword, postcode, device),
          group: sample.id, skippedInGroup: sample.skipped_units || 0,
          runId: sample.run_id || null, keyword, postcode, device,
          done: i < (sample.completed_units || 0), rows: [],
        });
      }
    }
    return searches;
  }

  const perRound = keywords.length * postcodes.length;
  for (let round = 1; round <= (job.rounds || 1); round++) {
    for (let i = 0; i < perRound; i++) {
      const keyword = keywords[Math.floor(i / postcodes.length)];
      const postcode = postcodes[i % postcodes.length];
      searches.push({
        key: searchKey(`round${round}`, keyword, postcode, null),
        group: "job", skippedInGroup: job.skipped_units || 0,
        runId: null, keyword, postcode, device: null,
        done: (round - 1) * perRound + i < (job.completed_units || 0), rows: [],
      });
    }
  }
  return searches;
}

function rowSearchKey(row) {
  return row.sample_id
    ? searchKey(row.sample_id, row.keyword, row.location, row.device)
    : searchKey(`round${row.round}`, row.keyword, row.location, null);
}

// Rows go to their search. A row for a search we didn't plan (e.g. a keyword
// edited after the scan) still counts, as an extra completed search.
function attachRows(searches, appearances) {
  const byKey = new Map(searches.map(s => [s.key, s]));
  for (const row of appearances) {
    const key = rowSearchKey(row);
    let search = byKey.get(key);
    if (!search) {
      search = {
        key, group: row.sample_id || "job", skippedInGroup: 0, runId: row.run_id || null,
        keyword: row.keyword, postcode: row.location, device: row.sample_id ? row.device || null : null,
        done: true, rows: [],
      };
      byKey.set(key, search);
      searches.push(search);
    }
    search.rows.push(row);
  }
}

// Each search's status: completed, failed, skipped or pending.
// Searches the Worker recorded (the "searches" table, since 14 Oct 2026) use
// their record; a search still waiting when the job was stopped is skipped.
// Older searches have no record, so it's worked out from the data (inferred):
// the database stored how many searches failed per sample, not which ones, so
// a done search with no rows on any channel is counted as failed, up to that
// sample's failed count (organic results nearly always come back).
function markOutcomes(searches, recorded, jobStatus) {
  const records = new Map();
  for (const r of recorded) {
    if (r && r.sample_id) records.set(searchKey(r.sample_id, r.keyword, r.postcode, r.device), r);
  }
  const failedSoFar = {};
  for (const search of searches) {
    const record = records.get(search.key);
    // A record still "pending" for a search that was done means its outcome
    // didn't get saved - fall back to working it out.
    if (record && (record.status !== "pending" || !search.done)) {
      search.status = record.status === "pending" && jobStatus === "stopped" ? "skipped" : record.status;
      search.finishedAt = record.finished_at || null;
      continue;
    }
    if (!search.done) { search.status = "pending"; continue; }
    search.inferred = true;
    const used = failedSoFar[search.group] || 0;
    if (search.rows.length === 0 && used < search.skippedInGroup) {
      search.status = "failed";
      failedSoFar[search.group] = used + 1;
    } else {
      search.status = "completed";
    }
  }
}

// ---------- Who each row belongs to ----------

function sellerName(raw) {
  if (!raw) return null;
  if (typeof raw.seller === "string" && raw.seller.trim()) return raw.seller.trim();
  if (raw.seller && raw.seller.seller_name) return String(raw.seller.seller_name).trim();
  if (typeof raw.merchant === "string" && raw.merchant.trim()) return raw.merchant.trim();
  if (raw.merchant && raw.merchant.name) return String(raw.merchant.name).trim();
  return null;
}

function rowDomain(row) {
  const fromSaved = brandOf(row.business_domain);
  if (fromSaved) return fromSaved;
  if (row.channel === "ppc" || row.channel === "seo") {
    return brandOf(domainOf(row.raw_data?.url, row.raw_data?.url_shown));
  }
  return null;
}

// Who every row belongs to, after the manual listing links.
function identifyAll(data) {
  return applyListingLinks(identifyRows(data.appearances || []), data.listingLinks || []);
}

// The raw_data fields identifyRows (and businessName) read. The niche roll-up's
// database function (audit_rollup_rows) sends only these, so add any new one
// there too; databaseRules.test.js checks they match.
export const IDENTITY_RAW_FIELDS = ["url", "url_shown", "listing_type", "seller", "merchant", "_name", "title", "name"];

// The advertiser keys seen in one job's rows ({ appearances, listingLinks }),
// grouped exactly as the reports group them. Rows that can't be tied to
// anyone are left out.
export function advertiserKeysSeen(data) {
  return new Set(advertiserChannelsSeen(data).keys());
}

// The same advertisers, each with the channels it was seen on:
// Map key -> Set("ppc" | "local" | "seo" | "shopping" | "lsa"). The keys are
// exactly buildAdvertisers' keys (every identified row, by its key).
export function advertiserChannelsSeen(data) {
  const out = new Map();
  for (const id of identifyAll(data).values()) {
    if (id.unidentified) continue;
    if (!out.has(id.key)) out.set(id.key, new Set());
    out.get(id.key).add(id.channel);
  }
  return out;
}

// The key a listing with no website is linked by: "name:<letters and digits>".
// The same for a listing kept under its own name and one matched by name ("likely").
export function listingKey(name) {
  return `name:${compact(name)}`;
}

// Returns a Map: row -> { channel, key, domain, name, matchedBy } or
// { channel, unidentified: true }. Local Services Ads rows are named by their
// business (counted since 15 Oct 2026, like Shopping: shown, not in visibility).
// Rows with a website are keyed by it. Rows without one are matched by name
// to a website seen in this job ("likely"), or kept under their own name.
// Paid Shopping ads with no seller can't be tied to anyone: they count in the
// SERP map only.
export function identifyRows(appearances) {
  const known = new Set();
  for (const row of appearances) {
    const d = rowDomain(row);
    if (d) known.add(d);
  }
  const knownDomains = [...known];

  const out = new Map();
  for (const row of appearances) {
    const channel = CHANNEL_OF_ROW[row.channel];
    if (!channel) continue;
    const raw = row.raw_data || {};

    const domain = rowDomain(row);
    if (domain) {
      out.set(row, { channel, key: domain, domain, name: null, matchedBy: "domain" });
      continue;
    }

    if (channel === "shopping" && raw.listing_type !== "free" && !sellerName(raw)) {
      out.set(row, { channel, unidentified: true });
      continue;
    }

    let name = channel === "shopping" ? sellerName(raw) : null;
    if (!name) name = row.raw_name && row.raw_name.trim() !== "Unknown" ? row.raw_name.trim() : businessName(raw, row.channel);
    if (!name || name === "Unknown" || !compact(name)) {
      out.set(row, { channel, unidentified: true });
      continue;
    }

    const likely = matchDomainByName(name, knownDomains);
    if (likely) {
      out.set(row, { channel, key: likely, domain: likely, name, matchedBy: "likely" });
    } else {
      out.set(row, { channel, key: listingKey(name), domain: null, name, matchedBy: "name" });
    }
  }
  return out;
}

// Applies the manual listing links (advertiser_links rows:
// { listing_key, domain, action: "link" | "unlink", status }) to the row
// identities from identifyRows, before anything is counted. Returns a new Map.
//   link:   a listing with no website, or a likely match, joins that website
//           (matchedBy "manual"). Manual beats likely.
//   unlink: a likely match is split back out under its own name (matchedBy "name").
// Only rows with a listing name are affected; rows with their own website never are.
export function applyListingLinks(identities, listingLinks) {
  const links = new Map();
  for (const l of listingLinks || []) {
    if (!l || !l.listing_key || (l.status && l.status !== "applied")) continue;
    if (l.action === "link" && brandOf(l.domain)) links.set(l.listing_key, { action: "link", domain: brandOf(l.domain) });
    if (l.action === "unlink") links.set(l.listing_key, { action: "unlink" });
  }
  if (links.size === 0) return identities;

  const out = new Map();
  for (const [row, id] of identities) {
    const link = !id.unidentified && id.name ? links.get(listingKey(id.name)) : null;
    if (link && link.action === "link") {
      out.set(row, { ...id, key: link.domain, domain: link.domain, matchedBy: "manual" });
    } else if (link && link.action === "unlink" && id.matchedBy === "likely") {
      out.set(row, { ...id, key: listingKey(id.name), domain: null, matchedBy: "name" });
    } else {
      out.set(row, id);
    }
  }
  return out;
}

// The job's map pack listings with no website of their own (kept under their
// own name, or matched by name as a "likely match"), for the "Link a map pack
// listing" panel. Whole job, before filters.
// [{ listingKey, name, postcodes, likelyDomain, linkedTo, basis, link }]
//   postcodes:    how many postcodes it was seen in
//   likelyDomain: the website AdAudit matched it to by name, or null
//   linkedTo:     the website it counts under now (after links), or null
//   basis:        "likely" | "manual" | null (kept separate)
//   link:         the saved link, { action, domain }, or null
export function listingsForLinking(data) {
  const appearances = data.appearances || [];
  const own = identifyRows(appearances);
  const linked = applyListingLinks(own, data.listingLinks || []);
  const saved = new Map();
  for (const l of data.listingLinks || []) {
    if (l && l.listing_key && (!l.status || l.status === "applied")) saved.set(l.listing_key, { action: l.action, domain: l.action === "link" ? brandOf(l.domain) : null });
  }

  const byKey = new Map();
  for (const row of appearances) {
    const id = own.get(row);
    if (!id || id.unidentified || !id.name || id.channel !== "local") continue;
    const key = listingKey(id.name);
    if (!byKey.has(key)) byKey.set(key, { listingKey: key, names: {}, postcodes: new Set(), likelyDomain: null, now: null });
    const entry = byKey.get(key);
    entry.names[id.name] = (entry.names[id.name] || 0) + 1;
    entry.postcodes.add(row.location);
    if (id.matchedBy === "likely") entry.likelyDomain = id.domain;
    entry.now = linked.get(row);
  }

  return [...byKey.values()].map(e => ({
    listingKey: e.listingKey,
    name: Object.entries(e.names).sort((x, y) => y[1] - x[1] || x[0].localeCompare(y[0]))[0][0],
    postcodes: e.postcodes.size,
    likelyDomain: e.likelyDomain,
    linkedTo: e.now.domain || null,
    basis: e.now.matchedBy === "manual" ? "manual" : e.now.matchedBy === "likely" ? "likely" : null,
    link: saved.get(e.listingKey) || null,
  })).sort((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase()));
}

// One entry per advertiser key, with its business type (whole job).
// linkedListings: the listings with no website joined to this website,
// { name, basis: "likely" | "manual" }.
export function buildAdvertisers(identities, overrides) {
  const advertisers = {};
  for (const id of identities.values()) {
    if (id.unidentified) continue;
    const a = advertisers[id.key] || (advertisers[id.key] = {
      key: id.key, domain: id.domain, names: {}, likelyNames: new Set(), manualNames: new Set(),
      hasDomainRow: false, hasMapListing: false,
    });
    if (id.matchedBy === "domain") a.hasDomainRow = true;
    if (id.matchedBy === "likely") a.likelyNames.add(id.name);
    if (id.matchedBy === "manual") a.manualNames.add(id.name);
    if (id.name) a.names[id.name] = (a.names[id.name] || 0) + 1;
    if (id.channel === "local") a.hasMapListing = true;
  }
  for (const a of Object.values(advertisers)) {
    a.matchedBy = !a.domain ? "name"
      : a.likelyNames.size > 0 ? "likely"
      : !a.hasDomainRow && a.manualNames.size > 0 ? "manual"
      : "domain";
    a.displayName = a.domain || Object.entries(a.names).sort((x, y) => y[1] - x[1] || x[0].localeCompare(y[0]))[0][0];
    a.linkedListings = [
      ...[...a.likelyNames].sort().map(name => ({ name, basis: "likely" })),
      ...[...a.manualNames].sort().map(name => ({ name, basis: "manual" })),
    ];
    a.businessType = classifyBusiness(a, BUILT_IN_LISTS, overrides);
  }
  return advertisers;
}

// The business type of an advertiser that may not have been seen at all
// (e.g. the job's own website): same rules, with no map pack listing.
export function classifyUnseen(key, typeOverrides) {
  const domain = key && !key.startsWith("name:") ? key : null;
  return classifyBusiness({ key, domain, hasMapListing: false }, BUILT_IN_LISTS, overridesByKey(typeOverrides));
}

// ---------- Advertiser rows ----------

function newStats() {
  return {
    channels: { ppc: new Map(), local: new Map(), seo: new Map(), shopping: new Map(), lsa: new Map() },
    withPrice: new Set(),
    coreSearches: new Set(),
    deviceSearches: { mobile: new Set(), desktop: new Set() },
    keywords: new Set(),
    postcodes: new Set(),
    firstSeenAt: null,
    lastSeenAt: null,
  };
}

function advertiserRow(a, s, of, ofByDevice) {
  const channels = {};
  for (const channel of CHANNELS) {
    const map = s.channels[channel];
    if (map.size === 0) { channels[channel] = null; continue; }
    const positions = [...map.values()].filter(p => p != null);
    channels[channel] = {
      searches: map.size,
      avgPosition: positions.length > 0 ? round(mean(positions), 1) : null,
      bestPosition: positions.length > 0 ? Math.min(...positions) : null,
    };
    if (channel === "shopping") channels.shopping.withPrice = s.withPrice.size;
  }
  return {
    rank: 0,
    key: a.key,
    domain: a.domain,
    displayName: a.displayName,
    matchedBy: a.matchedBy,
    likelyNames: [...a.likelyNames],
    businessType: a.businessType,
    visibility: {
      score: of > 0 ? Math.round((100 * s.coreSearches.size) / of) : 0,
      searches: s.coreSearches.size,
      of,
    },
    channels,
    devices: {
      mobile: { searches: s.deviceSearches.mobile.size, of: ofByDevice.mobile },
      desktop: { searches: s.deviceSearches.desktop.size, of: ofByDevice.desktop },
    },
    keywords: s.keywords.size,
    postcodes: s.postcodes.size,
    // Which ones, A to Z (for hover text).
    keywordList: [...s.keywords].sort((a, b) => a.localeCompare(b)),
    postcodeList: [...s.postcodes].sort((a, b) => a.localeCompare(b, "en", { numeric: true })),
    firstSeenAt: s.firstSeenAt,
    lastSeenAt: s.lastSeenAt,
    visibilityAppearances: CORE_CHANNELS.reduce((sum, c) => sum + (channels[c]?.searches || 0), 0),
  };
}

function bestAveragePosition(row) {
  const averages = CORE_CHANNELS.map(c => row.channels[c]?.avgPosition).filter(p => p != null);
  return averages.length > 0 ? Math.min(...averages) : Infinity;
}

// Most visible first; ties by number of channels, then best average position, then name.
function compareAdvertisers(a, b) {
  return b.visibility.searches - a.visibility.searches
    || CORE_CHANNELS.filter(c => b.channels[c]).length - CORE_CHANNELS.filter(c => a.channels[c]).length
    || bestAveragePosition(a) - bestAveragePosition(b)
    || a.displayName.localeCompare(b.displayName);
}

// The advertiser with the highest average position in a channel, among
// those seen in at least 2 searches there; null if nobody qualifies.
// Ties: more searches, then rank.
export function highestAveragePosition(rows, channel) {
  const qualifies = r => r.channels[channel]?.avgPosition != null && r.channels[channel].searches >= MIN_SEARCHES_FOR_POSITION;
  return rows.filter(qualifies).sort((a, b) =>
    a.channels[channel].avgPosition - b.channels[channel].avgPosition
    || b.channels[channel].searches - a.channels[channel].searches
    || a.rank - b.rank)[0] || null;
}

// ---------- Small helpers ----------

function numberOrNull(value) {
  const n = typeof value === "number" ? value : typeof value === "string" && value.trim() ? Number(value) : NaN;
  return Number.isFinite(n) ? n : null;
}

// Smallest of two positions, ignoring missing ones.
function minOf(a, b) {
  if (a == null) return b == null ? null : b;
  if (b == null) return a;
  return Math.min(a, b);
}

function hasPrice(raw) {
  if (!raw) return false;
  return Boolean(raw.price || raw.price_str || raw.seller?.price_str || raw.seller?.price);
}

function earliest(a, b) {
  if (!b) return a;
  if (!a) return b;
  return Date.parse(b) < Date.parse(a) ? b : a;
}

function latest(a, b) {
  if (!b) return a;
  if (!a) return b;
  return Date.parse(b) > Date.parse(a) ? b : a;
}

export function mean(values) {
  return values.reduce((x, y) => x + y, 0) / values.length;
}

export function round(value, places) {
  const f = 10 ** places;
  return Math.round(value * f) / f;
}
